import { FEATURE_STATUS, PLANE } from './dashboardFeatures';

/**
 * WHAT EACH FEATURE PLANE SHOWS IN ITS CHART BAND.
 *
 * This is the file to edit when you decide what the Shoorah / Company bands
 * should display. Each entry is one card; reorder, remove or add and the band
 * re-renders — no JSX changes.
 *
 *   type   'donut' | 'bar' | 'bigPercent' | 'rate' | 'list'
 *   span   how many of the 4 grid columns the card occupies
 *   build  (features, stats, accent) => props for that card type,
 *          or null to hide the card entirely
 *
 * `stats` is keyed by feature key and currently carries:
 *   { status, value, adoption, trend }
 * Adoption and value stay null until the metric adapters in
 * api/b2bDashboardApi.js are wired, so adoption-driven cards render their
 * empty state today and fill in automatically once that lands.
 */

const STATUS_SEGMENTS = [
  { key: FEATURE_STATUS.ON, label: 'On', color: '#07BF6F' },
  { key: FEATURE_STATUS.NEEDS_SETUP, label: 'Needs setup', color: '#92400E' },
  { key: FEATURE_STATUS.OFF, label: 'Off', color: '#BBBBBB' },
  { key: FEATURE_STATUS.NOT_ON_PLAN, label: 'Not on plan', color: '#878793' }
];

const countByStatus = (features, stats, status) =>
  features.filter((feature) => stats?.[feature.key]?.status === status).length;

/** Features that report a usable adoption figure, highest first. */
const rankedByAdoption = (features, stats) =>
  features
    .map((feature) => ({
      feature,
      adoption: stats?.[feature.key]?.adoption
    }))
    .filter((row) => typeof row.adoption === 'number')
    .sort((a, b) => b.adoption - a.adoption);

const statusDonut = (title) => ({
  type: 'donut',
  span: 1,
  build: (features, stats) => ({
    title,
    centreLabel: 'Features',
    emptyLabel: 'No features configured',
    segments: STATUS_SEGMENTS.map((segment) => ({
      label: segment.label,
      color: segment.color,
      value: countByStatus(features, stats, segment.key)
    }))
  })
});

const enabledShare = (label) => ({
  type: 'bigPercent',
  span: 1,
  build: (features, stats) => {
    const on = countByStatus(features, stats, FEATURE_STATUS.ON);
    return {
      label,
      percent: features.length ? (on / features.length) * 100 : null,
      caption: `${on} of ${features.length} switched on`
    };
  }
});

/**
 * Average adoption across the plane's features. Fills in automatically once
 * the metric adapters in api/b2bDashboardApi.js report `adoption`.
 */
const adoptionRate = () => ({
  type: 'rate',
  span: 2,
  build: (features, stats, accent) => {
    const ranked = rankedByAdoption(features, stats);
    const average = ranked.length
      ? (ranked.reduce((sum, row) => sum + row.adoption, 0) / ranked.length) * 100
      : null;

    return {
      title: 'Adoption rate',
      rowLabel: 'Average across features',
      percent: average,
      caption: ranked.length
        ? `${ranked.length} of ${features.length} features reporting`
        : 'No usage data yet',
      accent
    };
  }
});

/** Share of the plane's features that are switched on. */
const enabledRate = () => ({
  type: 'rate',
  span: 2,
  build: (features, stats, accent) => {
    const on = countByStatus(features, stats, FEATURE_STATUS.ON);
    return {
      title: 'Features enabled',
      rowLabel: 'Switched on',
      percent: features.length ? (on / features.length) * 100 : null,
      caption: `${on} of ${features.length} available to your people`,
      accent
    };
  }
});

/** Bars stay readable up to about ten columns; past that the labels collide. */
const BAR_LIMIT = 10;

const adoptionBar = (title, subtitle) => ({
  type: 'bar',
  span: 2,
  build: (features, stats, accent) => {
    const ranked = rankedByAdoption(features, stats).slice(0, BAR_LIMIT);
    return {
      title,
      subtitle,
      accent,
      categories: ranked.length
        ? ranked.map((row) => row.feature.name)
        : features.slice(0, BAR_LIMIT).map((feature) => feature.name),
      data: ranked.length ? ranked.map((row) => Math.round(row.adoption * 100)) : [],
      valueSuffix: '%',
      seriesName: 'Adoption'
    };
  }
});

/**
 * Per-plane card lists. Both planes currently mirror each other — swap freely
 * once you've decided what each should actually surface.
 */
/**
 * OTHER CARDS WORTH CONSIDERING (each needs the noted data first):
 *   Most used features   → `list` type; needs `adoption` or `value` per feature
 *   Devices connected    → new stat; needs a wearable/device count per company
 *   Health data enabled  → new stat; needs the health-integration flag
 * The `list` card type is already wired in PlaneAnalyticsBand, so "Most used
 * features" is one entry here once adoption lands.
 */
export const PLANE_CHARTS = {
  [PLANE.SHOORAH]: [
    adoptionRate(),
    enabledRate(),
    statusDonut('Shoorah features by status'),
    enabledShare('Shoorah features enabled'),
    adoptionBar('Adoption by feature', 'Share of active users, highest first')
  ],
  [PLANE.COMPANY]: [
    adoptionRate(),
    enabledRate(),
    statusDonut('Company features by status'),
    enabledShare('Company features enabled'),
    adoptionBar('Adoption by feature', 'Share of active users, highest first')
  ]
};

export const getPlaneCharts = (plane) => PLANE_CHARTS[plane] || [];
